import { JuloUtils } from "../common"
import { PackageLeasingRule } from "./package-leasing-rule"
import {
  ServiceBikeBrakeSystem,
  ServiceBikeGearSystem,
  ServiceBikeType,
} from "./service-bike"
import {
  FrontSuspensionRequirement,
  ServiceBikePart,
  ServiceBikePartGearSystemRequirement,
  isPartPreselectedOnBikeType,
  partExistsOnBikeType,
} from "./service-bike-part"
import { ServicePackage } from "./service-package"
import { ServicePackagePriceResolver } from "./service-package-price-resolver"
import { ServiceAction } from "./service-part-action"

export interface ServicePackageBikeFilters {
  bike: {
    isElectric?: boolean | null
    type: ServiceBikeType
    customBikeTypeId?: string | null
    brakeSystemFront?: ServiceBikeBrakeSystem | null
    brakeSystemBack?: ServiceBikeBrakeSystem | null
    gearSystem?: ServiceBikeGearSystem | null
    hasFrontGears?: boolean | null
    hasDamperFront?: boolean | null
    hasDamperBack?: boolean | null
  }
  priceListId?: string
  isClubMember?: boolean
}

export interface ServicePackageFilters extends ServicePackageBikeFilters {
  /** The job's leasing company labels; empty for customers and non-leasing jobs. */
  leasingCompanyIds: string[]
}

interface PartToCheck {
  partId: string
  action: ServiceAction
}
interface IncludedPart {
  partId: string
  includedActions: ServiceAction[]
}

export class ServicePackageUtils {
  static partActionIsPartOfPackage(
    part: PartToCheck,
    allIncludedParts: IncludedPart[],
  ): boolean {
    return allIncludedParts.some((includedPart) => {
      if (part.partId !== includedPart.partId) return false
      return includedPart.includedActions.includes(part.action)
    })
  }

  static isAvailableForLeasingCompanies(
    pkg: Pick<ServicePackage, "leasingRule" | "leasingCompanyIds">,
    leasingCompanyIds: string[],
  ): boolean {
    const matchesListedCompany = pkg.leasingCompanyIds.some((id) =>
      leasingCompanyIds.includes(id),
    )
    switch (pkg.leasingRule) {
      case PackageLeasingRule.ALL:
        return true
      case PackageLeasingRule.ONLY_FOR:
        return matchesListedCompany
      case PackageLeasingRule.EXCLUDED_FOR:
        return !matchesListedCompany
    }
  }

  static filter(
    packages: ServicePackage[],
    { leasingCompanyIds, ...bikeFilters }: ServicePackageFilters,
    allPartsInfo: ServiceBikePart[] = [],
  ): ServicePackage[] {
    return ServicePackageUtils.filterByBike(
      packages.filter((pkg) =>
        ServicePackageUtils.isAvailableForLeasingCompanies(
          pkg,
          leasingCompanyIds,
        ),
      ),
      bikeFilters,
      allPartsInfo,
    )
  }

  /**
   * Like filter, but without the leasing rule. For showing a package that was
   * already picked, such as narrowing its items on a card.
   */
  static filterByBike(
    packages: ServicePackage[],
    {
      bike: {
        type,
        customBikeTypeId,
        isElectric,
        brakeSystemFront,
        brakeSystemBack,
        gearSystem,
        hasFrontGears,
        hasDamperFront,
        hasDamperBack,
      },
      isClubMember = false,
      priceListId,
    }: ServicePackageBikeFilters,
    allPartsInfo: ServiceBikePart[] = [],
  ): ServicePackage[] {
    const indexedParts = JuloUtils.indexBy(allPartsInfo, "id")

    // Filter packages based on whether any price entry matches the bike and price list
    const filteredPackages = packages.filter((pkg) => {
      return ServicePackagePriceResolver.isPackageAvailableForBike(
        pkg,
        {
          type,
          customBikeTypeId: customBikeTypeId ?? null,
          isElectric: isElectric ?? null,
          gearSystem: gearSystem ?? null,
          isClubMember,
        },
        priceListId,
      )
    })

    if (filteredPackages.length === 0) return filteredPackages

    return filteredPackages.map((pkg) => {
      return {
        ...pkg,
        items: pkg.items.filter((item) => {
          const part = indexedParts[item.partId]
          if (!part) return true
          const {
            gearSystemRequirement,
            foundOnGearSystems,
            foundOnFrontBrakeSystems,
            foundOnRearBrakeSystems,
            frontSuspensionRequirement,
            requiresRearSuspension,
            requiresElectricBike,
          } = part

          const bikeTypeMatch =
            partExistsOnBikeType(part, type) &&
            isPartPreselectedOnBikeType(part, type)

          const electricMatch =
            !requiresElectricBike ||
            (requiresElectricBike && isElectric === true)

          let gearSystemMatch = true
          if (gearSystem !== undefined && gearSystem !== null) {
            gearSystemMatch =
              foundOnGearSystems.length === 0 ||
              foundOnGearSystems.includes(gearSystem)
          }

          let brakeSystemFrontMatch = true
          if (brakeSystemFront !== undefined && brakeSystemFront !== null) {
            brakeSystemFrontMatch =
              foundOnFrontBrakeSystems.length === 0 ||
              foundOnFrontBrakeSystems.includes(brakeSystemFront)
          }

          let brakeSystemBackMatch = true
          if (brakeSystemBack !== undefined && brakeSystemBack !== null) {
            brakeSystemBackMatch =
              foundOnRearBrakeSystems.length === 0 ||
              foundOnRearBrakeSystems.includes(brakeSystemBack)
          }

          let frontSuspensionMatch = true
          if (hasDamperFront !== undefined && hasDamperFront !== null) {
            switch (frontSuspensionRequirement) {
              case FrontSuspensionRequirement.REQUIRES_FRONT_SUSPENSION:
                frontSuspensionMatch = hasDamperFront
                break
              case FrontSuspensionRequirement.REQUIRES_NO_FRONT_SUSPENSION:
                frontSuspensionMatch = !hasDamperFront
                break
              default:
                frontSuspensionMatch = true
            }
          }

          let rearSuspensionMatch = true
          if (hasDamperBack !== undefined && hasDamperBack !== null) {
            rearSuspensionMatch =
              !requiresRearSuspension ||
              (requiresRearSuspension && hasDamperBack)
          }

          let frontGearsMatch = true
          if (hasFrontGears !== undefined && hasFrontGears !== null) {
            switch (gearSystemRequirement) {
              case ServiceBikePartGearSystemRequirement.MUST_HAVE_FRONT_GEARS:
                frontGearsMatch = hasFrontGears
                break
              case ServiceBikePartGearSystemRequirement.MUST_NOT_HAVE_FRONT_GEARS:
                frontGearsMatch = !hasFrontGears
                break
              default:
                frontGearsMatch = true
            }
          }

          return (
            bikeTypeMatch &&
            electricMatch &&
            gearSystemMatch &&
            brakeSystemFrontMatch &&
            brakeSystemBackMatch &&
            frontSuspensionMatch &&
            rearSuspensionMatch &&
            frontGearsMatch
          )
        }),
      }
    })
  }
}
